import type { ScLinkField, TypedComponentRendering } from '@core/jss-models';
import { getFieldValue } from '@core/jss-utils';
import type { AnchorElementTarget } from '@essent/current-web';
import type {
  RichTextField,
  TextField,
} from '@sitecore-jss/sitecore-jss-angular';

type SmartChargingLeadWidgetRenderingFields = {
  // General
  backToStep?: TextField;
  of?: TextField;
  chargingStationsInfoLink?: ScLinkField;
  smartChargingInfoLink?: ScLinkField;
  missingDataTitle?: TextField;
  missingDataBody?: RichTextField;
  // Step 1
  step1Title?: TextField;
  step1InputLabel?: TextField;
  step1InputHelp?: TextField;
  step1OptionYes?: TextField;
  step1OptionNo?: TextField;
  step1InfoTitle?: TextField;
  step1InfoBody?: TextField;
  step1PrimaryButton?: TextField;
  step1InputRequiredError?: TextField;
  step1RejectedTitle?: TextField;
  step1RejectedBody?: RichTextField;
  // Step 2
  step2Title?: TextField;
  step2InputLabel?: TextField;
  step2InputHelp?: TextField;
  step2AdditionalText?: TextField;
  step2PrimaryButton?: TextField;
  step2InputRequiredError?: TextField;
  step2InvalidValueError?: TextField;
  step2LicensePlateFound?: TextField;
  step2LicensePlateError?: TextField;
  step2RejectedTitle?: TextField;
  step2RejectedBody?: TextField;
  step2AcceptedTitle?: TextField;
  step2AcceptedBody?: RichTextField;
  step2AcceptedPrimaryButton?: TextField;
  step2AcceptedSecondaryButton?: TextField;
  step2SupportedBrandsModalLink?: TextField;
  step2UnknownTitle?: TextField;
  step2UnknownBody?: RichTextField;
  step2ServerErrorTitle?: TextField;
  step2ServerErrorBody?: TextField;
  // Step 3
  step3Title?: TextField;
  step3Body?: RichTextField;
};

type LinkFieldValue = {
  href: string;
  text: string;
  linktype: 'internal' | 'external';
  url: string;
  anchor: string;
  target: AnchorElementTarget;
  title: string;
};

export type SmartChargingLeadWidgetRendering =
  TypedComponentRendering<SmartChargingLeadWidgetRenderingFields>;

export function mapSmartChargingLeadWidgetContent(
  rendering: SmartChargingLeadWidgetRendering
) {
  return {
    backToStep: getFieldValue(rendering, 'backToStep', ''),
    of: getFieldValue(rendering, 'of', ''),
    chargingStationsInfoLink: getFieldValue<LinkFieldValue>(
      rendering,
      'chargingStationsInfoLink'
    ),
    smartChargingInfoLink: getFieldValue<LinkFieldValue>(
      rendering,
      'smartChargingInfoLink'
    ),
    missingDataTitle: getFieldValue(rendering, 'missingDataTitle', ''),
    missingDataBody: getFieldValue(rendering, 'missingDataBody', ''),
    step1: {
      title: getFieldValue(rendering, 'step1Title', ''),
      inputLabel: getFieldValue(rendering, 'step1InputLabel', ''),
      inputHelp: getFieldValue(rendering, 'step1InputHelp', ''),
      optionYes: getFieldValue(rendering, 'step1OptionYes', ''),
      optionNo: getFieldValue(rendering, 'step1OptionNo', ''),
      infoTitle: getFieldValue(rendering, 'step1InfoTitle', ''),
      infoBody: getFieldValue(rendering, 'step1InfoBody', ''),
      primaryButton: getFieldValue(rendering, 'step1PrimaryButton', ''),
      inputRequiredError: getFieldValue(
        rendering,
        'step1InputRequiredError',
        ''
      ),
      rejectedTitle: getFieldValue(rendering, 'step1RejectedTitle', ''),
      rejectedBody: getFieldValue(rendering, 'step1RejectedBody', ''),
    },
    step2: {
      title: getFieldValue(rendering, 'step2Title', ''),
      inputLabel: getFieldValue(rendering, 'step2InputLabel', ''),
      inputHelp: getFieldValue(rendering, 'step2InputHelp', ''),
      additionalText: getFieldValue(rendering, 'step2AdditionalText', ''),
      primaryButton: getFieldValue(rendering, 'step2PrimaryButton', ''),
      inputRequiredError: getFieldValue(
        rendering,
        'step2InputRequiredError',
        ''
      ),
      invalidValueError: getFieldValue(rendering, 'step2InvalidValueError', ''),
      licensePlateFound: getFieldValue(rendering, 'step2LicensePlateFound', ''),
      licensePlateError: getFieldValue(rendering, 'step2LicensePlateError', ''),
      rejectedTitle: getFieldValue(rendering, 'step2RejectedTitle', ''),
      rejectedBody: getFieldValue(rendering, 'step2RejectedBody', ''),
      acceptedTitle: getFieldValue(rendering, 'step2AcceptedTitle', ''),
      acceptedBody: getFieldValue(rendering, 'step2AcceptedBody', ''),
      acceptedPrimaryButton: getFieldValue(
        rendering,
        'step2AcceptedPrimaryButton',
        ''
      ),
      acceptedSecondaryButton: getFieldValue(
        rendering,
        'step2AcceptedSecondaryButton',
        ''
      ),
      supportedBrandsModalLink: getFieldValue(
        rendering,
        'step2SupportedBrandsModalLink',
        ''
      ),
      unknownTitle: getFieldValue(rendering, 'step2UnknownTitle', ''),
      unknownBody: getFieldValue(rendering, 'step2UnknownBody', ''),
      serverErrorTitle: getFieldValue(rendering, 'step2ServerErrorTitle', ''),
      serverErrorBody: getFieldValue(rendering, 'step2ServerErrorBody', ''),
    },
    step3: {
      title: getFieldValue(rendering, 'step3Title', ''),
      body: getFieldValue(rendering, 'step3Body', ''),
    },
  };
}
